import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TabBar, TabItem } from '@wot/components';
import { useEffect, useState } from 'react';
import plansApi from '../../../api/plans';
import { SPINNER } from '../../../assets/animated';
import NoResults from '../../../components/NoResults';
import Pagination from '../../../components/Pagination';
import usePagainate from '../../../hooks/usePaginate';
import { useTab } from '../../../hooks/useTab';
import TableContainer from './components/TableContainer';

interface IFilters {
  categoryType: string,
  modeOfPayment: string,
  amount: number,
  amountRange: string
}

export type ISorting = "createdAt" | "studentName"

function ChangeRequest() {
  const queryClient = useQueryClient();
  const { activeTab, onTabChange } = useTab('all');
  const { currentPage, resultsPerPage, nextPage, prevPage, setResultsPerPage, resetPage } = usePagainate();
  const [sortBy, setSortBy] = useState<{ [key: string]: number }>({ "createdAt": -1 });
  const [filters, setFilters] = useState<{}>({});
  const handleSort = (key: ISorting) => {
    setSortBy({ [key]: sortBy[key] === 1 ? -1 : 1 });
  };

  const { data, isLoading, isError, isPreviousData } = useQuery({
    queryKey: ['change-requests-plans', currentPage, resultsPerPage, activeTab, sortBy, filters],
    queryFn: async () =>
      await plansApi.getChangeRequests(
        currentPage,
        resultsPerPage,
        activeTab === 'all'
          ? { ...filters }
          : { status: activeTab, ...filters },
        { ...sortBy },
      )
  });

  useEffect(() => {
    if (!isPreviousData) {
      queryClient.prefetchQuery({
        queryKey: ['change-requests-plans', currentPage + 1],
        queryFn: async () =>
          await plansApi.getChangeRequests(
            currentPage,
            resultsPerPage,
            activeTab === 'all'
              ? { ...filters }
              : { status: activeTab, ...filters },
            { ...sortBy },
          )
      });
    }
  }, [data, isPreviousData, currentPage, resultsPerPage, queryClient, isError]);

  const filterResults = (filters: any) => {
    setFilters(filters);
  }

  if (isError) {
    return (
      <div className="w-full min-h-[256px] flex items-center justify-center">
        <p className="font-medium">Something went Wrong.</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-white h-full rounded-xl py-6">
      <h2 className="font-bold text-xl px-2">Change Requests</h2>
      <div className="w-full h-full flex flex-col">
        <div className="my-4 flex items-center justify-between px-2">
          <TabBar value={activeTab} onChange={onTabChange} textColor="text-black" indicatorColor="bg-primary">
            <TabItem value={'all'} label="All" />
            <TabItem value={'pending'} label="New" />
            <TabItem value={'approved'} label="Accepted" />
            <TabItem value={'rejected'} label="Rejected" />
          </TabBar>
        </div>
        {isLoading ? (
          <div className="w-full min-h-[256px] flex items-center justify-center">
            <img src={SPINNER} alt="loading" className="w-6 h-6" />
          </div>
        ) : data.results.length > 0 ? (
          <>
            <TableContainer handleSort={handleSort} data={data.results} />
            <Pagination
              count={data.count}
              currentPage={currentPage}
              nextPage={nextPage}
              prevPage={prevPage}
              resultsPerPage={resultsPerPage}
              setResultsPerPage={setResultsPerPage}
            />
          </>
        ) : (
          <NoResults />
        )}
      </div>
    </div>
  );
}

export default ChangeRequest;
